import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { designerAPI, scoreAPI } from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { 
  TrendingUp, 
  ChevronRight,
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  Users,
  User,
  Building2,
  Calendar,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MONTHS, cn, getPerformanceLevel, getPerformanceScoreBg } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Performance = () => {
  const { isDark } = useTheme();
  const currentDate = new Date();
  const [designers, setDesigners] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedSubTeam, setSelectedSubTeam] = useState('all');
  const [expandedTeams, setExpandedTeams] = useState({});

  const [reportPeriod, setReportPeriod] = useState('6');
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Get months labels for display
  const getMonthsForPeriod = (period) => {
    const months = [];
    const count = parseInt(period);
    for (let i = count - 1; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      months.push({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        label: `${MONTHS[date.getMonth()]} ${date.getFullYear()}`
      });
    }
    return months;
  };

  const reportMonths = getMonthsForPeriod(reportPeriod);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [designersRes, scoresRes] = await Promise.all([
          designerAPI.getAll(),
          scoreAPI.getAll({ year: selectedYear })
        ]);
        setDesigners(designersRes.data);
        setScores(scoresRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedYear]);

  // Get unique teams and sub-teams
  const teams = [...new Set(designers.map(d => d.vertical_team).filter(Boolean))];
  const subTeams = [...new Set(designers.map(d => d.sub_team).filter(Boolean))];

  // Filter designers by team
  const filteredDesigners = designers.filter(d => {
    if (selectedTeam !== 'all' && d.vertical_team !== selectedTeam) return false;
    if (selectedSubTeam !== 'all' && d.sub_team !== selectedSubTeam) return false;
    return true;
  });

  // Group designers by team/sub-team
  const groupedByTeam = filteredDesigners.reduce((acc, designer) => {
    const team = designer.sub_team || designer.vertical_team || 'Unassigned';
    if (!acc[team]) acc[team] = [];
    acc[team].push(designer);
    return acc;
  }, {});

  // Calculate weekly averages for a designer
  const getDesignerWeeklyAverages = (designerId) => {
    const designerScores = scores.filter(s => s.designer_id === designerId);
    const weeklyData = {};
    
    designerScores.forEach(score => {
      const key = `${score.year}-${score.month}-W${score.week}`;
      weeklyData[key] = {
        year: score.year,
        month: score.month,
        week: score.week,
        performance_score: score.performance_score || 0,
        contribution_score: score.contribution_score || 100,
        ranking_score: score.ranking_score || 0,
        performance_level: score.performance_level || getPerformanceLevel(score.performance_score || 0)?.label || '',
        active_days: score.active_days || 5
      };
    });
    
    return weeklyData;
  };

  // Get current month's average for a designer
  const getCurrentMonthAverage = (designerId) => {
    // Use the selected year, and for month use the latest month with scores
    const designerScores = scores.filter(s => s.designer_id === designerId);
    
    if (designerScores.length === 0) return null;
    
    // Find the latest month with data for this designer
    const latestScore = designerScores.reduce((latest, s) => {
      const scoreDate = s.year * 12 + s.month;
      const latestDate = latest ? latest.year * 12 + latest.month : 0;
      return scoreDate > latestDate ? s : latest;
    }, null);
    
    if (!latestScore) return null;
    
    // Get all scores for this designer in the latest month
    const monthScores = designerScores.filter(s => 
      s.year === latestScore.year && s.month === latestScore.month
    );
    
    const avgPerformance = monthScores.reduce((sum, s) => sum + (s.performance_score || 0), 0) / monthScores.length;
    const avgContribution = monthScores.reduce((sum, s) => sum + (s.contribution_score || 100), 0) / monthScores.length;
    const avgRanking = monthScores.reduce((sum, s) => sum + (s.ranking_score || 0), 0) / monthScores.length;
    
    return {
      performance: Math.round(avgPerformance * 10) / 10,
      contribution: Math.round(avgContribution * 10) / 10,
      ranking: Math.round(avgRanking * 10) / 10,
      weeks: monthScores.length
    };
  };

  // Toggle team expansion
  const toggleTeam = (team) => {
    setExpandedTeams(prev => ({
      ...prev,
      [team]: !prev[team]
    }));
  };

  // Expand all teams
  const expandAll = () => {
    const allExpanded = {};
    Object.keys(groupedByTeam).forEach(team => {
      allExpanded[team] = true;
    });
    setExpandedTeams(allExpanded);
  };

  // Collapse all teams
  const collapseAll = () => {
    setExpandedTeams({});
  };

  // Download via backend API
  const downloadReport = async (format) => {
    const setLoading = format === 'excel' ? setDownloadingExcel : setDownloadingPDF;
    setLoading(true);
    try {
      const token = localStorage.getItem('arrise_token');
      const params = new URLSearchParams({ period: reportPeriod });
      if (selectedTeam !== 'all') params.append('team', selectedTeam);
      if (selectedSubTeam !== 'all') params.append('sub_team', selectedSubTeam);

      const response = await axios.get(
        `${BACKEND_URL}/api/reports/team-performance/${format}?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename=(.+)/);
      const filename = filenameMatch ? filenameMatch[1] : `Team_Performance_${reportPeriod}_Months.${format === 'excel' ? 'xlsx' : 'pdf'}`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      alert(`Failed to download ${format} file. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="team-performance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white font-outfit">
            Team Performance
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Weekly performance averages by team and designer
          </p>
        </div>
        
        <div className="flex flex-wrap gap-3">
          {/* Year Select */}
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="perf-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Team Filter */}
          <Select value={selectedTeam} onValueChange={setSelectedTeam}>
            <SelectTrigger className="w-40" data-testid="perf-team-select">
              <SelectValue placeholder="All Teams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {teams.map(team => (
                <SelectItem key={team} value={team}>{team}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sub-Team Filter */}
          <Select value={selectedSubTeam} onValueChange={setSelectedSubTeam}>
            <SelectTrigger className="w-40" data-testid="perf-subteam-select">
              <SelectValue placeholder="All Sub-Teams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sub-Teams</SelectItem>
              {subTeams.map(team => (
                <SelectItem key={team} value={team}>{team}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Report Downloads (Left) + Summary Stats (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left: Report Download Section */}
        <Card className={cn("lg:col-span-1", isDark ? "bg-slate-800 border-slate-700" : "")}>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Download className="w-5 h-5 text-[#331062]" />
              <h3 className={cn("font-semibold", isDark ? "text-white" : "text-slate-800")}>
                Download Reports
              </h3>
            </div>

            <div className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
              {reportMonths[0]?.label} &mdash; {reportMonths[reportMonths.length - 1]?.label}
            </div>

            {/* Period Selector */}
            <Select value={reportPeriod} onValueChange={setReportPeriod}>
              <SelectTrigger className="w-full" data-testid="report-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">Last 6 Months</SelectItem>
                <SelectItem value="12">Last 12 Months</SelectItem>
              </SelectContent>
            </Select>

            {/* Download Buttons - stacked */}
            <Button
              variant="outline"
              className="w-full gap-2 justify-start"
              onClick={() => downloadReport('excel')}
              disabled={downloadingExcel}
              data-testid="download-excel-btn"
            >
              {downloadingExcel ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-emerald-600" />}
              {downloadingExcel ? 'Downloading...' : 'Download Excel'}
            </Button>
            <Button
              className="w-full gap-2 justify-start bg-[#331062] hover:bg-[#4a1a8a]"
              onClick={() => downloadReport('pdf')}
              disabled={downloadingPDF}
              data-testid="download-pdf-btn"
            >
              {downloadingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              {downloadingPDF ? 'Downloading...' : 'Download PDF'}
            </Button>
          </CardContent>
        </Card>

        {/* Right: Summary Stats */}
        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#331062]/10">
                <Users className="w-5 h-5 text-[#331062]" />
              </div>
              <div>
                <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>
                  {filteredDesigners.length}
                </p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                  Total Designers
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Building2 className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>
                  {Object.keys(groupedByTeam).length}
                </p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                  Teams/Sub-Teams
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>
                  {scores.filter(s => s.year === currentDate.getFullYear() && s.month === currentDate.getMonth() + 1).length}
                </p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                  Scores This Month
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Calendar className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>
                  {MONTHS[currentDate.getMonth()]}
                </p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                  Current Month
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* Expand/Collapse Controls */}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={expandAll} className="gap-1">
          <ChevronDown className="w-4 h-4" /> Expand All
        </Button>
        <Button variant="outline" size="sm" onClick={collapseAll} className="gap-1">
          <ChevronRight className="w-4 h-4" /> Collapse All
        </Button>
      </div>

      {/* Team-wise Performance */}
      <div className="space-y-4">
        {Object.entries(groupedByTeam).map(([team, teamDesigners]) => {
          const isExpanded = expandedTeams[team];
          
          // Calculate team average
          const teamScores = scores.filter(s => 
            teamDesigners.some(d => d.id === s.designer_id) &&
            s.year === currentDate.getFullYear() &&
            s.month === currentDate.getMonth() + 1
          );
          const teamAvgPerf = teamScores.length > 0 
            ? (teamScores.reduce((sum, s) => sum + (s.performance_score || 0), 0) / teamScores.length).toFixed(1)
            : '-';

          return (
            <Card key={team} className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
              {/* Team Header */}
              <button
                onClick={() => toggleTeam(team)}
                className={cn(
                  "w-full p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors rounded-t-lg",
                  !isExpanded && "rounded-b-lg"
                )}
                data-testid={`team-toggle-${team.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-lg",
                    isDark ? "bg-[#331062]/30" : "bg-[#331062]/10"
                  )}>
                    <Building2 className="w-5 h-5 text-[#331062]" />
                  </div>
                  <div className="text-left">
                    <h3 className={cn("font-semibold", isDark ? "text-white" : "text-slate-800")}>
                      {team}
                    </h3>
                    <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                      {teamDesigners.length} members • Avg Performance: {teamAvgPerf}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "px-3 py-1 rounded-full text-sm font-medium",
                    parseFloat(teamAvgPerf) >= 80 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" :
                    parseFloat(teamAvgPerf) >= 60 ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" :
                    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                  )}>
                    {teamAvgPerf !== '-' ? `${teamAvgPerf}/100` : 'No Data'}
                  </span>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </button>

              {/* Team Members */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className={cn("border-t", isDark ? "border-slate-700" : "border-slate-200")}>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className={cn(
                              "text-left text-xs uppercase tracking-wider",
                              isDark ? "bg-slate-700/50 text-slate-400" : "bg-slate-50 text-slate-500"
                            )}>
                              <th className="py-3 px-4">Designer</th>
                              <th className="py-3 px-4">Role</th>
                              <th className="py-3 px-4 text-center">Weeks Scored</th>
                              <th className="py-3 px-4 text-center">Avg Performance</th>
                              <th className="py-3 px-4 text-center">Avg Contribution</th>
                              <th className="py-3 px-4 text-center">Avg Ranking</th>
                              <th className="py-3 px-4 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody className={cn("divide-y", isDark ? "divide-slate-700" : "divide-slate-200")}>
                            {teamDesigners.map(designer => {
                              const monthAvg = getCurrentMonthAverage(designer.id);
                              
                              return (
                                <tr 
                                  key={designer.id}
                                  className={cn(
                                    "hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors",
                                  )}
                                >
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-3">
                                      <div className={cn(
                                        "w-9 h-9 rounded-full flex items-center justify-center",
                                        isDark ? "bg-slate-600" : "bg-slate-200"
                                      )}>
                                        <User className="w-5 h-5 text-slate-500" />
                                      </div>
                                      <div>
                                        <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>
                                          {designer.name}
                                        </p>
                                        <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>
                                          {designer.employee_id || 'N/A'}
                                        </p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className={cn("py-3 px-4 text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                                    {designer.role}
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    <span className={cn(
                                      "px-2 py-1 rounded text-sm",
                                      monthAvg ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" : "text-slate-400"
                                    )}>
                                      {monthAvg ? monthAvg.weeks : '-'}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    {monthAvg ? (
                                      <span className={cn(
                                        "px-2 py-1 rounded font-medium text-sm",
                                        getPerformanceScoreBg(monthAvg.performance)
                                      )}>
                                        {monthAvg.performance}
                                      </span>
                                    ) : (
                                      <span className="text-slate-400">-</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    {monthAvg ? (
                                      <span className="text-green-600 dark:text-green-400 font-medium">
                                        {monthAvg.contribution}%
                                      </span>
                                    ) : (
                                      <span className="text-slate-400">-</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    {monthAvg ? (
                                      <span className={cn("font-bold", isDark ? "text-white" : "text-slate-800")}>
                                        {monthAvg.ranking}
                                      </span>
                                    ) : (
                                      <span className="text-slate-400">-</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    <Link 
                                      to={`/performance/${designer.id}`}
                                      className="inline-flex items-center gap-1 text-[#331062] hover:text-[#4a1a8a] text-sm font-medium"
                                      data-testid={`view-detail-${designer.id}`}
                                    >
                                      View Details
                                      <ChevronRight className="w-4 h-4" />
                                    </Link>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {Object.keys(groupedByTeam).length === 0 && (
        <Card className={cn("p-8 text-center", isDark ? "bg-slate-800 border-slate-700" : "")}>
          <Users className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          <h3 className={cn("text-lg font-medium mb-2", isDark ? "text-white" : "text-slate-800")}>
            No Designers Found
          </h3>
          <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
            No designers match the selected filters.
          </p>
        </Card>
      )}
    </div>
  );
};

export default Performance;
